import type { ToolApprovalBatch } from "#components/ui/tool-approval/renderers/ToolApprovalRenderer.types.ts";
import { extractToolCallStatus, humanizeToolName } from "#components/ui/tool-approval/toolApprovalDecision.ts";
import { getToolNameKey } from "#features/conversations/types/assistantError.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type { PreparedActionStatus, ToolApprovalCall } from "#types/types.ts";
import { Text } from "@hopper-ui/components";
import { IconCheck, IconMinus, IconTriangleExclamation } from "@intentui/icons";
import classNames from "classnames";
import "./ToolApprovalBatch.css";

interface Props {
    batch: ToolApprovalBatch;
    calls: { call: ToolApprovalCall; status: PreparedActionStatus; isUnsettled?: boolean }[];
}

const STATUS_LABELS: Partial<Record<PreparedActionStatus, string>> = {
    sent: "preparedAction.sent",
    failed: "preparedAction.failed",
    discarded: "preparedAction.discarded",
    cancelled: "preparedAction.cancelled",
    approved: "preparedAction.awaitingOutcome"
};

/** A listed batch once it is answered: one line per call with how it ended, and the counts. */
export function ToolApprovalBatchSummary({ batch, calls }: Props) {
    const { tShared } = useSharedTranslation();
    // An approved call whose result never landed counts as failed, as it reads on its own card.
    const lines = calls.map(({ call, status, isUnsettled }) => (status === "approved" && isUnsettled
        ? { call, status: "failed" as const, label: "preparedAction.notConfirmed" }
        : { call, status, label: STATUS_LABELS[status] ?? "preparedAction.sent" }));
    const counts = [
        { key: "toolApproval.summaryDone", count: lines.filter(({ status }) => status === "sent").length },
        { key: "toolApproval.summaryDiscarded", count: lines.filter(({ status }) => status === "discarded").length },
        { key: "toolApproval.summaryFailed", count: lines.filter(({ status }) => status === "failed").length }
    ].filter(({ count }) => count > 0);

    return (
        <div className="wai-prepared-action wai-pa-approval" role="group" aria-label={tShared("toolApproval.summaryLabel")}>
            <ul className="wai-ta-summary-lines">
                {lines.map(({ call, status, label }) => (
                    <li key={call.toolCallId} className={classNames("wai-ta-summary-line", `is-${status}`)}>
                        {status === "sent" ? <IconCheck aria-hidden="true" /> : status === "failed" ? <IconTriangleExclamation aria-hidden="true" /> : <IconMinus aria-hidden="true" />}
                        <Text size="sm" fontWeight="body-sm-medium" color="neutral" className="wai-ta-summary-title">
                            {batch.title?.(call.args) ?? extractToolCallStatus(call.args).title ?? tShared(getToolNameKey(call.toolName), { defaultValue: humanizeToolName(call.toolName) })}
                        </Text>
                        <Text size="xs" color="neutral-weak">{tShared(label)}</Text>
                    </li>
                ))}
            </ul>
            <Text size="xs" color="neutral-weak">{counts.map(({ key, count }) => tShared(key, { count })).join(", ")}</Text>
        </div>
    );
}
